import type { DefaultMap } from "@glideapps/ts-necessities";
import { assert, definedMap, hasOwnProperty, panic } from "@glideapps/ts-necessities";
import type { AppAnalyticsEvents } from "../analytics/app-events";
import type { ChangeObservable } from "../change-obserable";
import type { ComponentIndexes } from "../component-indexes";
import type { Row, Table } from "../computation-model/data";
import type { NamespaceGraph, NamespaceGraphNode } from "../computation-model/namespace-graph";
import type { KeyPath, RootPath, ValueAndFormatPaths } from "../computation-model/paths";
import type { TableKeeper } from "../computation-model/table-keeper";
import type { TableKeeperStore } from "../computation-model/table-keeper-store";
import type { MutableNamespace } from "../computation-model/types";
import { ConditionVariable } from "../condition-variable";
import type {
    Database,
    DataSnapshot,
    DocumentDataWithID,
    PaymentInformationForBuyButtons,
    PublishedAppSnapshot,
    RowIndex,
} from "../Database";
import type { TableName } from "../database-strings";
import type { EminenceFlags } from "../Database/eminence";
import type { AllQuotaValues, QuotaKind, QuotaValues } from "../Database/quotas";
import type { Formula, TableColumn, TableGlideType, TypeSchema } from "../description";
import type { AppLoginTokenContainer, AppSnapshotURLs, UploadComponentKind } from "../firebase-function-types";
import type { LocationSettings } from "../location-common";
import type { DeviceFormFactor } from "../render/form-factor";
import type { Sound } from "../sound";
import type { SourceMetadata } from "../source-metadata";
import type { JSONData, JSONObject } from "../Support";
import type { YesCodeValue } from "../yes-code-types";
import type * as helpers from "./component-helpers";
import * as types from "./computation-types";
import * as data from "./data";
import type { AppAuthentication, AppFeatures, AppKind, EmailClient, IconImage, ScreenFlag } from "./SerializedApp";
import type { TableIndex } from "./table-index";

export type AppOther = UI | Action | ConditionVariable;

export type Evaluable = types.Evaluable<AppOther, AppEnvironment>;
export type EnvironmentBoundEvaluable = types.EnvironmentBoundEvaluable<AppOther, AppEnvironment>;
export type EvalEnvironment = types.EvalEnvironment<AppOther, AppEnvironment>;
export type RootEvalEnvironment = types.RootEvalEnvironment<AppOther, AppEnvironment>;
export type ValueProducer = types.ValueProducer<AppOther, AppEnvironment>;
export type ValueProducers = types.ValueProducers<AppOther, AppEnvironment>;
export type Context = types.Context<AppOther, AppEnvironment>;
export type Value = types.Value<AppOther, AppEnvironment>;
export type PotentialValue = types.PotentialValue<AppOther, AppEnvironment>;
export type ObjectValue = types.ObjectValue<AppOther, AppEnvironment>;
export type ForceValueProducer = types.ForceValueProducer<AppOther, AppEnvironment>;
export type SubscribeValueProducer = helpers.SubscribeValueProducer<AppOther, AppEnvironment>;
export type SubscribableValueProducer = helpers.SubscribableValueProducer<AppOther, AppEnvironment>;
export type ObjectInspector = types.ObjectInspector<AppOther, AppEnvironment>;

export type Loading = types.Loading;
export const LOADING = types.LOADING;

export type ActionSource = types.ActionSource;
// eslint-disable-next-line @typescript-eslint/no-redeclare
export const ActionSource = types.ActionSource;

export type Data = data.Data<AppOther, AppEnvironment>;
// eslint-disable-next-line @typescript-eslint/no-redeclare
export const Data = data.Data;

export interface UIClass {
    new (componentName: string, ...args: (EnvironmentBoundEvaluable | undefined)[]): UI;
    spec: types.DeserializationSpec<"ui">;
}

export interface ActionClass {
    new (...args: (EnvironmentBoundEvaluable | undefined)[]): Action;
}

export enum MapLocationKind {
    LatLong,
    Address,
}

interface LatLongMapLocation {
    readonly kind: MapLocationKind.LatLong;
    readonly latitude: number;
    readonly longitude: number;
}

interface AddressMapLocation {
    readonly kind: MapLocationKind.Address;
    readonly address: string;
}

export type MapLocation = LatLongMapLocation | AddressMapLocation;

export interface NativeTableSnapshot {
    readonly rows: readonly DocumentDataWithID[];
    // indexed by app ID
    readonly appDataVersions: Record<string, number>;
}

export abstract class AppFacilities {
    public abstract get locationSettings(): LocationSettings;
    public abstract renderProgress(): React.ReactElement;
    public abstract alert(message: string): void;
    public abstract openURL(url: string): void;
    public abstract mapURL(location: MapLocation): string | undefined;
    public abstract openMap(location: MapLocation): void;
    public abstract mailtoURL(
        emailClient: EmailClient,
        to: string | undefined,
        subject: string | undefined,
        body: string | undefined,
        cc: string | undefined,
        bcc: string | undefined
    ): string | undefined;
    public abstract fetchJSON(url: string): Promise<JSONData | undefined>;
    public abstract isSignedInToFirebase(): Promise<boolean>;
    public abstract getAuthUserID(): Promise<string | undefined>;
    public abstract getAppUserEmail(): Promise<string | undefined>;
    public abstract getIDToken(): Promise<string | undefined>;
    public abstract callAuthCloudFunction(
        functionName: string,
        body: any,
        headers?: { [key: string]: string },
        stringify?: boolean
    ): Promise<Response | undefined>;
    public abstract callAuthIfAvailableCloudFunction(
        functionName: string,
        body: any,
        headers: { [key: string]: string },
        stringify?: boolean
    ): Promise<Response | undefined>;
    public abstract signInWithCustomToken(token: string): Promise<boolean>;
    public abstract trackEvent<Name extends keyof AppAnalyticsEvents>(
        event: Name,
        options: AppAnalyticsEvents[Name]
    ): void;
    public abstract callYesCode(
        url: string,
        params: ReadonlyMap<string, unknown>
    ): YesCodeValue | undefined | Promise<YesCodeValue | undefined>;

    // FIXME: This does not belong in here.  Anything specific to an app
    // should be in the app environment.
    public abstract setSnapshotLocationsForAppID(appID: string, locations: AppSnapshotURLs): void;
    public abstract loadDataSnapshot(appID: string): Promise<DataSnapshot | undefined>;
    public abstract loadNativeTableSnapshot(
        appID: string,
        tableName: TableName
    ): Promise<NativeTableSnapshot | undefined>;
    // FIXME: This is not called in the frontend anywhere.  Shouldn't we at
    // least call it in the builder when an app is closed?
    public abstract purgeDataSnapshot(appID: string): void;
    public abstract loadPublishedAppFromLocalStorage(appID: string): PublishedAppSnapshot | undefined;
    public abstract loadPublishedAppSnapshot(appID: string): Promise<PublishedAppSnapshot | undefined>;
    public abstract persistPublishedApp(appID: string, publishedApp: PublishedAppSnapshot): void;
    public abstract setInitialSchemaForAppID(appID: string, schema: TypeSchema): void;
    public abstract getInitialSchemaForAppID(appID: string): TypeSchema | undefined;
}

export abstract class UI extends types.UnsubscribableValueProducer<AppOther, AppEnvironment> implements Evaluable {
    public abstract render(context: Context, renv: RenderEnvironment, key?: string | number): React.ReactElement;

    public get current(): Value {
        return this;
    }

    public evalInEnvironment(_env: EvalEnvironment, _context: Context): ValueProducer {
        return this;
    }

    public getObjectInspector(): ObjectInspector | Loading | undefined {
        return undefined;
    }
}

export class UIEvaluable extends types.Callable<AppOther, AppEnvironment> implements Evaluable {
    constructor(private readonly _uiClass: UIClass, private readonly _args: (Evaluable | undefined)[]) {
        super();
    }

    public evalInEnvironment(env: EvalEnvironment, _context: Context): ValueProducer {
        return new this._uiClass(
            this._uiClass.spec[0],
            ...this._args.map(a => definedMap(a, d => new types.BindEnvironmentEvaluable(d, env)))
        );
    }

    public call(args: EnvironmentBoundEvaluable[], context: Context, rootEnv: RootEvalEnvironment): ValueProducer {
        assert(args.length === 0, "UI with arguments cannot be called with arguments");
        return this.evalInEnvironment(rootEnv, context);
    }
}

export enum ActionResult {
    CopiedToClipboard = "copied-to-clipboard",
    ZapierTrigger = "zapier-trigger",
}

export abstract class Action extends types.UnsubscribableValueProducer<AppOther, AppEnvironment> implements Evaluable {
    public abstract run(
        context: Context,
        env: RenderEnvironment,
        actionSource: ActionSource,
        handled?: boolean
    ): Promise<ActionResult | undefined>;

    public canExecute(_context: Context, _force: ForceValueProducer): boolean {
        return true;
    }

    // Returns [mutatesData, reallyNeedsOnline]
    //
    // Actions that mutate data won't work offline, except in add/form screens
    // if the app can add rows offline.  Actions that really need online won't
    // work offline anywhere.  We assume that `!mutatesData` implies
    // `!reallyNeedsOnline`, i.e. you can't have `[false, true]`.
    public needsOnline(_context: Context, _force: ForceValueProducer, _env: RenderEnvironment): [boolean, boolean] {
        return [false, false];
    }

    public targetURL(_context: Context, _env: RenderEnvironment, _actionSource: ActionSource): string | undefined {
        return undefined;
    }

    public get current(): Value {
        return this;
    }

    public evalInEnvironment(_env: EvalEnvironment, _context: Context): ValueProducer {
        return this;
    }

    public get isNoAction(): boolean {
        return false;
    }

    // ##autoRunInTabletMode:
    // In tablet mode, top-level list screens auto-push
    // their first item detail screen so that the detail screen is always
    // present, unless the list is empty.  This flag, then, is only true for
    // actions which push detail screens.
    //
    // FIXME: Composite actions right now don't auto-run, though arguably they
    // should if one of their constituent actions want to.
    public get autoRunInTabletMode(): boolean {
        return false;
    }

    public getObjectInspector(): ObjectInspector | Loading | undefined {
        return undefined;
    }
}

export abstract class OnlineOnlyAction extends Action {
    public needsOnline(_context: Context, _force: ForceValueProducer): [boolean, boolean] {
        return [true, true];
    }
}

export class ActionEvaluable extends types.Callable<AppOther, AppEnvironment> implements Evaluable {
    constructor(private readonly _actionClass: ActionClass, private readonly _args: (Evaluable | undefined)[]) {
        super();
    }

    public evalInEnvironment(env: EvalEnvironment, _context: Context): ValueProducer {
        return new this._actionClass(
            ...this._args.map(a => definedMap(a, d => new types.BindEnvironmentEvaluable(d, env)))
        );
    }

    public call(args: EnvironmentBoundEvaluable[], context: Context, rootEnv: RootEvalEnvironment): ValueProducer {
        assert(args.length === 0, "Action with arguments cannot be called with arguments");
        return this.evalInEnvironment(rootEnv, context);
    }
}

export interface EvaluatedMenuItem {
    readonly title: ValueProducer; //                    string
    readonly icon: ValueProducer; //                     string
    readonly onTap: Action; //                           Action
    readonly isEnabled: ValueProducer | undefined; //    boolean
    readonly isVisible: ValueProducer | undefined; //    boolean
    readonly href: ValueProducer | undefined; //         string
    readonly style: ValueProducer | undefined; //        string
    readonly purpose: ValueProducer | undefined; //      string
}

export interface EvaluatedScreen {
    readonly title: ValueProducer | undefined;
    readonly ui: ValueProducer;
    readonly inputCtx: Context;
    readonly ctx: Context;
    readonly name: string | undefined;
    readonly topBar: ValueProducer | undefined;
    readonly pivotPicker: ValueProducer | undefined;
    readonly quotaBannerMessage: ValueProducer;
    readonly menuItems: ReadonlyArray<EvaluatedMenuItem>;
    readonly flags: ReadonlySet<ScreenFlag>;
    // We need this right now to get a unique-ish screen name to save
    // the scroll position.
    readonly screenId: string;
    readonly itemID: string | undefined;
    needsRerender: boolean;
}

export interface PathWithThunksFlag {
    readonly rootPath: RootPath;
    readonly usesThunks: boolean;
}

export type PathForColumn =
    | ({
          readonly isGlobal: false;
          readonly columnPath: KeyPath;
          readonly tableColumnPath: RootPath;
      } & PathWithThunksFlag)
    | {
          readonly isGlobal: true;
          readonly rootPath: RootPath;
          // When we delete temporary columns for column configuration, we
          // only delete columns that have this set.
          readonly canBeDeleted: boolean;
          readonly usesThunks: false;
      };

export interface TemporaryComputedColumn {
    readonly pathForValue: PathForColumn | undefined;
    readonly pathForFormat: PathForColumn | undefined;
}

export type RecomputeTiming = Map<
    string, // NOTE: table name
    Map<
        string, // NOTE: column name
        {
            time: number;
        }
    >
>;

export interface RecomputeResult {
    readonly tableData: Map<TableGlideType, Table>;
    readonly stats: RecomputeTiming;
}

export interface ComputationModel {
    // FIXME: Probably don't expose this - see comment below
    readonly ns: MutableNamespace<unknown>;

    // FIXME: We probably don't want to expose this, either, since it allows
    // making table keepers for any arbitrary table name, which then won't get
    // added to the namespace.
    readonly tableKeeperStore: TableKeeperStore<TableKeeper>;

    // FIXME: Either go all in on the computation model exposing the
    // namespace, or don't do it at all.  If we do the former,
    // we should remove `getDataForTable` in favor of `getPathForTable`.
    getDataForTable(tableName: TableName): Table | undefined;
    getBaseDataForTable(tableName: TableName): Table | undefined;

    getPathForTable(tableName: TableName): RootPath | undefined;
    getBasePathForTable(tableName: TableName): RootPath | undefined;

    getColumnPaths(tableName: TableName): ReadonlyMap<string, ValueAndFormatPaths> | undefined;

    getUserProfileRowPath(): RootPath | undefined;

    getTimestampPath(): RootPath;
    getStartOrEndOfTodayPath(startOrEnd: "start" | "end"): RootPath;
    getVerifiedEmailAddressPath(): RootPath;
    getRealEmailAddressPath(): RootPath;

    buildTemporaryComputedColumn(
        tableName: TableName,
        column: TableColumn,
        columnIsNew: boolean
    ): TemporaryComputedColumn | undefined;
    deleteTemporaryComputedColumn(t: TemporaryComputedColumn): void;

    makeGraph(): NamespaceGraph;
    debugPrintNode(node: NamespaceGraphNode): void;

    // NOTE: We are only using this for debugging and performance analysis at the moment
    recompute(setAllDirty: boolean, printTiming: boolean, useStrictMode: boolean): RecomputeResult;
}

export interface DataStore {
    readonly schema: TypeSchema;

    getComputationModelObservable(forBuilder: boolean): ChangeObservable<ComputationModel | undefined>;

    fetchTableRows(tableName: TableName, forBuilder: boolean): ValueProducer;
    fetchSingleLinkRow(link: ValueProducer, forBuilder: boolean): ValueProducer;
    fetchMultipleLinkRows(link: ValueProducer, forBuilder: boolean): ValueProducer;

    // We only use this in the builder for now.
    getFirstRowObservable(tableName: TableName): ChangeObservable<Row | undefined> | undefined;

    // FIXME: This is only necessary because we don't store the compiled
    // computed columns in the serialized app.
    setAppEnvironment(appEnvironment: MinimalAppEnvironment): void;
    // This is called by the forwarding app environment when its delegate
    // changes.
    appEnvironmentUpdated(): void;

    addRowToTable(
        tableName: TableName,
        values: ValueProducer,
        columnNames: ReadonlySet<string> | undefined,
        addToData: boolean,
        fromDataEditor: boolean
    ): Promise<{ didAdd: boolean; rowVP: ValueProducer | undefined; row: Row | undefined }>;
    setColumnsInRow(
        tableName: TableName,
        rowIndex: RowIndex,
        values: ValueProducer,
        setUnderlyingData: boolean,
        withDebounce: boolean,
        fromDataEditor: boolean
    ): Promise<void>;
    deleteRowAtIndex(
        tableName: TableName,
        rowIndex: RowIndex,
        setUnderlyingData: boolean,
        fromDataEditor: boolean
    ): Promise<void>;

    deleteRow(tableName: TableName, row: ValueProducer, fromDataEditor: boolean): Promise<void>;
    clearTable(tableName: TableName): Promise<void>;

    modifyComputedColumns(
        removed: ReadonlyMap<string, readonly string[]>,
        modified: ReadonlyMap<string, readonly TableColumn[]>,
        moved: ReadonlyMap<string, readonly [string, number][]>,
        setProtected: ReadonlyMap<string, readonly [string, boolean][]>,
        rowIDColumns: ReadonlyMap<string, string | undefined>
    ): void;
    setEmailOwnersColumns(tableName: TableName, emailOwnersColumns: readonly string[]): void;

    addComputedColumnsToData(tableName: TableName, data: Data): void;

    // This is used in the Data Editor to reset the schema
    setSchema(schema: TypeSchema | undefined): void;
    // Will rebuild the computation model.  Called from the app environment.
    userProfileTableChanged(): void;

    isRowOwnedByUser(tableName: TableName, row: Row): boolean;

    // This is specifically for FirestoreDataStore, which needs to re-load
    // from snapshots and listeners on quota changes.
    // ##dataStoreRowQuotaChange
    resetFromUpstream(): void;
    // FIXME: Add methods telling the datastore to start/stop listening
}

export type ColumnFormatChangedCallback = () => void;

export enum ComponentMoveAction {
    NewSpanAbove = "new-span-above",
    NewSpanBelow = "new-span-below",
    NewColumnLeft = "new-column-left",
    NewColumnRight = "new-column-right",
    InsertIntoColumnBefore = "insert-into-column-before",
    InsertIntoColumnAfter = "insert-into-column-after",
}

export interface UserProfileTableInfo {
    readonly tableName: TableName;
    readonly emailColumnName: string;
    // FIXME: Shouldn't these two be optional, at least the image column?
    readonly nameColumnName: string;
    readonly imageColumnName: string;
    readonly rolesColumnName: string | undefined;
}

export interface OfflineQueue<T> {
    enqueue(item: T): void;
    // If `processItem` did process the item, it must return `true`, in which
    // case the queue removes the item.
    processIfOnline(processItem: (item: T) => Promise<boolean>): Promise<void>;
    forEach(processItem: (item: T) => void): void;
    retire(): void;
}

export interface MinimalAppEnvironment {
    readonly appID: string;
    readonly appKind: AppKind;
    readonly appFeatures: AppFeatures;
    readonly appFacilities: AppFacilities;
    readonly sourceMetadata: readonly SourceMetadata[] | undefined;
    readonly userProfileTableInfo: UserProfileTableInfo | undefined;
    readonly authenticator: AppUserAuthenticator;
    readonly database: Database;
    readonly dataStore: DataStore;
    readonly locationSettings: LocationSettings | undefined;
    readonly eminenceFlags: EminenceFlags;

    makeOfflineQueue?<T>(
        name: string,
        onOnline: () => void,
        idForItem: (item: T) => string
    ): OfflineQueue<T> | undefined;

    // FIXME: should not be optional
    getQuotaValues?(kind: QuotaKind): QuotaValues | undefined;

    getComputedColumn?(
        schema: TypeSchema,
        tableName: TableName,
        columnName: string,
        formula: Formula,
        isBuilder: boolean,
        forDataEditor: boolean
    ): Evaluable | undefined;
    modifyColumnsInSchema?(
        oldSchema: TypeSchema,
        removed: ReadonlyMap<string, readonly string[]>,
        modified: ReadonlyMap<string, readonly TableColumn[]>,
        moved: ReadonlyMap<string, readonly [string, number][]>,
        setProtected: ReadonlyMap<string, readonly [string, boolean][]>,
        rowIDColumns: ReadonlyMap<string, string | undefined>
    ): { schema: TypeSchema; errors: readonly string[] };

    makeRootEvalEnvironment?(): RootEvalEnvironment;

    // FIXME: should not be optional
    callYesCode?(
        url: string,
        params: ReadonlyMap<string, unknown>
    ): YesCodeValue | undefined | Promise<YesCodeValue | undefined>;

    isGeocodeWithinQuota(key: string): boolean;
    // FIXME: should not be optional
    countGeocodeForQuota?(key: string): void;
    // This is for analytics purposes.
    // FIXME: should not be optional
    reportGeocode?(): void;

    updateApp?(
        appFeatures: AppFeatures,
        sourceMetadata: readonly SourceMetadata[] | undefined,
        userProfileTableInfo: UserProfileTableInfo | undefined
    ): void;
}

export interface AppEnvironmentCallbacks extends MinimalAppEnvironment {
    pushBuilderScreen(
        name: string,
        ctx: Context,
        title: Evaluable | undefined,
        env: RenderEnvironment,
        flags: ReadonlySet<ScreenFlag> | undefined,
        itemID: string | undefined
    ): void;
    pushModalBuilderScreen(
        name: string,
        ctx: Context,
        title: Evaluable | undefined,
        flags: ReadonlySet<ScreenFlag> | undefined
    ): void;
    pushMenuScreen(
        menuID: string,
        primaryKey: string,
        defaultScreenName: string,
        ctx: Context,
        env: RenderEnvironment
    ): void;
    popScreen(env: RenderEnvironment): void;
    popModalScreen(env: RenderEnvironment): void;
    popAnyScreen(env: RenderEnvironment): void;
    popToRoot(env: RenderEnvironment): void;
    pushFeedbackScreen(env: RenderEnvironment): void;
    pushUserProfileScreen(): void;
    readonly currentPivot: number;
    setCurrentTab(index: number, env: RenderEnvironment): void;
    setCurrentPivot(index: number, env: RenderEnvironment): void;
    showToast(text: string, isSuccess: boolean): void;
    playSound(name: Sound): void;
    copyToClipboard(str: string): void;
    readonly paymentInformation: PaymentInformationForBuyButtons;
    readonly shortName: string | undefined;
    readonly customDomain: string | undefined;
    readonly authentication: AppAuthentication | undefined;
    readonly isPublished: boolean;
    readonly deviceFormFactor: DeviceFormFactor;
    readonly demonstratingProComponents: boolean;
    suppressNotificationTopic(topic: string): void;
    unsuppressNotificationTopic(topic: string): void;
    // This only works in the builder.  Must call the callback immediately.
    registerColumnFormatChangedCallback(
        tableName: TableName,
        columnName: string,
        callback: ColumnFormatChangedCallback
    ): void;
    unregisterColumnFormatChangedCallback(
        tableName: TableName,
        columnName: string,
        callback: ColumnFormatChangedCallback
    ): void;
    getColumnFormat(tableName: TableName, columnName: string): EnvironmentBoundEvaluable | undefined;
    addImageComponent(
        screenName: string | undefined,
        componentIndex: ComponentIndexes | undefined,
        imageURL: string
    ): void;
    addComponentForColumn(
        screenName: string | undefined,
        componentIndex: ComponentIndexes | undefined,
        columnName: string
    ): void;
    addComponentWithUniqueName(
        screenName: string | undefined,
        componentIndex: ComponentIndexes | undefined,
        uniqueName: string
    ): void;
    // These are actually ##componentPositionIDs.
    componentMoved(sourceComponentID: string, targetComponentID: string, action: ComponentMoveAction): void;
    startBuilderBusy(): void;
    stopBuilderBusy(): void;
    makeRenderEnvironment(): RenderEnvironment | undefined;
}

export interface AppEnvironmentDelegate extends AppEnvironmentCallbacks {
    modifyAppFeatures(appFeatures: AppFeatures): AppFeatures;
    makeGlobalVariable(variableName: string): ValueProducer | undefined;
    updateGlobalVariables(globalVariables: DefaultMap<string, ValueProducer>): void;
    quotasDidUpdate?(quotas: AllQuotaValues, prior: AllQuotaValues): void;
}

export enum ComponentHighlight {
    None,
    App,
    Designer,
}

export enum QuotaBannerMessage {
    List = "list",
    Map = "map",
}

export type ResponseStatus = "Success" | "Forbidden" | "Offline";

export interface QuotaBannerRequirements {
    readonly needListQuota: boolean;
    readonly needMapQuota: boolean;
}

export type OAuth2TokenAuth = Readonly<{
    email: string;
    displayName: string | undefined;
    password: string | undefined;
    userProfileRow: JSONObject | undefined;
    appLoginToken: AppLoginTokenContainer | undefined;
}>;
export type AppUserChangedCallback = (
    appUserID: string | undefined,
    realEmail: string | undefined,
    virtualEmail: string | undefined,
    loginToken: AppLoginTokenContainer | undefined
) => void;

export interface AppUserAuthenticator {
    readonly appFacilities: AppFacilities;
    readonly appID: string;
    readonly appUserID: string | undefined;
    readonly realEmail: string | undefined;
    readonly virtualEmail: string | undefined;
    // This only exists so that the Preview-As text entry can save whatever
    // the user enters and have it be preserved.  Ideally this would be
    // managed in that text entry, not here.
    readonly unnormalizedVirtualEmail: string | undefined;
    readonly userProfileRow: JSONObject | undefined;
    readonly failedSignIns: number;

    addCallback(callback: AppUserChangedCallback): void;
    removeCallback(callback: AppUserChangedCallback): void;

    sendPinForEmail(email: string, glideCommit: string): Promise<ResponseStatus>;
    getPasswordForOAuth2Token(authToken: string, userAgreed: boolean): Promise<OAuth2TokenAuth | undefined>;
    getPasswordForEmailPin(
        email: string,
        pin: string,
        userAgreed: boolean
    ): Promise<{ password: string; loginToken: AppLoginTokenContainer | undefined } | undefined>;
    authorizeForAppWithEmail(email: string, passwordForEmail: string): Promise<boolean>;
    authorizeForAppWithPassword(password: string): Promise<boolean>;
    authorizeForAppWithLoginToken(
        loginToken: AppLoginTokenContainer,
        email: string | undefined,
        password: string | undefined
    ): Promise<boolean>;
    tryGetAppUserID(): Promise<void>;
    setEmailAddressFromBuilder(email: string, isSelectedByUser: boolean): void;
}

export type UploadProgressHandler = (totalBytes: number, bytesSent: number) => void;

export interface UploadFileResponseError {
    readonly error: Error;
    readonly quotaExceeded: boolean;
}

export type UploadFileResponse = { path: string } | UploadFileResponseError;

export function isUploadFileResponseError(x: UploadFileResponse): x is UploadFileResponseError {
    return hasOwnProperty(x, "error");
}

export interface UploadSession {
    cancel(): void;
    attempt(): Promise<UploadFileResponse>;
}

export interface AppEnvironment extends AppEnvironmentCallbacks {
    readonly appFeatures: AppFeatures;
    readonly localDataStore: DataStore;
    readonly isBuilder: boolean;
    readonly appIcon: IconImage;
    readonly appUserID: string | undefined;
    readonly tableIndexes: Map<string, TableIndex<AppOther, AppEnvironment>>;
    readonly globalVariables: DefaultMap<string, ValueProducer>;
    readonly globalValues: Map<string, ValueProducer>;
    readonly fallbackUsername: ValueProducer;
    readonly enableShareScreen: boolean;
    readonly shareScreenTitle: string;

    getHighlightComponentFlag(componentID: string): ValueProducer;

    // These are for analytics purposes.
    reportGeocode(): void;
    getHighestGeocodeCount(): number;
    // We use these to start background refreshing on-demand and listening
    // to quota documents.
    // The data editor will need full table refreshing, even if the app
    // can deal with partial table refreshing.
    isMounted(inEditor: boolean): void;
    isUnmounted(inEditor: boolean): void;

    getQuotaReachedFlag(kind: QuotaKind): ValueProducer;

    reportQuotaIncrease(kind: QuotaKind, increment: number): void;

    // FIXME: Replace all of this with the two quota methods above
    getGeocodeQuotaReachedFlag(): ValueProducer;
    getGeocodeQuotaReachedForKeyFlag(key: string): ValueProducer;

    uploadAppFile(
        file: File,
        component: UploadComponentKind,
        onProgress: UploadProgressHandler | undefined
    ): UploadSession;

    signOut(): void;
}

export function showProComponents(appEnvironment: AppEnvironment): boolean {
    const { demonstratingProComponents, eminenceFlags } = appEnvironment;
    return eminenceFlags.proComponents || demonstratingProComponents;
}

export type DisplayContext = "default" | "master" | "detail" | "modal" | "map-master" | "map-detail";

interface RenderFlags {
    columnWidth?: number;
    needsRerender: boolean;
    omitOverlays?: boolean;
    registerSearchBarClearer?: (clearFunc: () => void) => void;
    needsPush?: boolean;
    sizeClass: DeviceFormFactor;
    displayContext: DisplayContext;
}

export class RenderEnvironment {
    constructor(
        readonly appEnvironment: AppEnvironment,
        readonly flags: RenderFlags,
        public readonly screenFlags: ReadonlySet<ScreenFlag> | undefined
    ) {}

    public with(flags: Partial<RenderFlags> | undefined, screenFlags?: ReadonlySet<ScreenFlag>) {
        return new RenderEnvironment(this.appEnvironment, { ...this.flags, ...flags }, screenFlags ?? this.screenFlags);
    }

    public get sizeClass(): DeviceFormFactor {
        return this.flags.sizeClass;
    }

    public get displayContext(): DisplayContext {
        return this.flags.displayContext;
    }

    public get needsPush(): boolean {
        return this.flags.needsPush === true;
    }

    public get omitOverlays(): boolean {
        return this.flags.omitOverlays === true;
    }

    public get columnWidth(): number | undefined {
        return this.flags.columnWidth;
    }

    public renderProgress(): React.ReactElement {
        return this.appEnvironment.appFacilities.renderProgress();
    }

    public registerSearchBarClearer(): ((clearFunc: () => void) => void) | undefined {
        return this.flags.registerSearchBarClearer;
    }
}

export function asUI(v: Value): UI {
    if (!(v instanceof UI)) {
        return panic("Value is not a UI");
    }
    return v;
}

export function asAction(v: PotentialValue): Action {
    if (!(v instanceof Action)) {
        return panic("Value is not an Action");
    }
    return v;
}

export function asMaybeAction(v: PotentialValue): Action | undefined {
    if (!(v instanceof Action)) {
        return undefined;
    }
    return v;
}

export function asConditionVariable(v: PotentialValue): ConditionVariable {
    if (!(v instanceof ConditionVariable)) {
        return panic("Value is not a ConditionVariable");
    }
    return v;
}

class ContextBoundUI extends UI {
    constructor(private readonly _ui: UI, private readonly _context: Context) {
        super();
    }

    public render(_context: Context, renv: RenderEnvironment): React.ReactElement {
        return this._ui.render(this._context, renv);
    }
}

class ContextBoundAction extends Action {
    constructor(private readonly _action: Action, private readonly _context: Context) {
        super();
    }

    public run(
        _context: Context,
        appEnv: RenderEnvironment,
        actionSource: ActionSource,
        handled?: boolean
    ): Promise<ActionResult | undefined> {
        return this._action.run(this._context, appEnv, actionSource, handled);
    }

    public targetURL(_context: Context, env: RenderEnvironment, actionSource: ActionSource): string | undefined {
        return this._action.targetURL(this._context, env, actionSource);
    }

    public get isNoAction(): boolean {
        return this._action.isNoAction;
    }

    public canExecute(_ctx: Context, force: ForceValueProducer): boolean {
        return this._action.canExecute(this._context, force);
    }

    public needsOnline(_ctx: Context, force: ForceValueProducer, env: RenderEnvironment): [boolean, boolean] {
        return this._action.needsOnline(this._context, force, env);
    }
}

// FIXME: See the comment on `initComputation`.
function makeContextBound<O, A>(value: types.Value<O, A>, context: types.Context<O, A>): types.PotentialValue<O, A> {
    if (value instanceof UI) {
        return new ContextBoundUI(value, context as any) as any;
    } else if (value instanceof Action) {
        return new ContextBoundAction(value, context as any) as any;
    }
    return undefined;
}

types.initComputation(v => v instanceof UI || v instanceof Action || v instanceof ConditionVariable, makeContextBound);

export interface BasicUserProfile {
    readonly email: string;
    readonly name?: string;
    readonly image?: string;
}

export enum MenuItemPurpose {
    AddRow = "add-item",
    EditRow = "edit-item",
    SaveRow = "save-item",
    SetValue = "set-value",
    FilterAndSearch = "filter-and-search",
    SignOut = "sign-out",
}
